import React, { ReactElement, RefObject, useCallback } from 'react';
import { parse } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { Container, Label, LabelBackground, InputField } from './styled';
import { useDateTimeInput } from '../..';
import { Merge } from '../../../../../utilities/types';

export const DateTimeTextInput: React.FC<DateTimeTextInputProps> = ({
  prefix,
  ...inputProps
}) => {
  const {
    onBlur,
    disabled,
    error,
    input,
    setInput,
    setInputDate,
    onChange,
    dateTimeFormat,
    use12HourMode,
    hideTime,
    timeZone,
    isActive,
    label,
    t,
  } = useDateTimeInput();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInput(event.target.value);

      try {
        // Accounts for the single 'A' depicting the AM/PM postfix
        const expectedLength =
          use12HourMode && !hideTime
            ? dateTimeFormat?.length + 1
            : dateTimeFormat.length;
        if (event.target.value.length !== expectedLength)
          throw new Error('Format not matched');

        const date = parse(event.target.value, dateTimeFormat, new Date());

        onChange(timeZone ? zonedTimeToUtc(date, timeZone) : date);
      } catch {
        onChange(new Date(''));
        setInputDate(undefined);
      }
    },
    [
      dateTimeFormat,
      onChange,
      setInput,
      setInputDate,
      timeZone,
      use12HourMode,
      hideTime,
    ],
  );

  const placeholder = dateTimeFormat.toLocaleLowerCase();

  return (
    <Container active={!!isActive} disabled={!!disabled} error={!!error}>
      {prefix && <div>{prefix}</div>}
      <InputField
        {...inputProps}
        autoComplete="off"
        placeholder={t ? t(placeholder) : placeholder}
        value={input || ''}
        readOnly={disabled}
        disabled={disabled}
        onChange={handleChange}
        onBlur={onBlur}
      />
      <Label atTop disabled={disabled}>
        <LabelBackground />
        {label}
      </Label>
    </Container>
  );
};

export type DateTimeTextInputProps = Merge<
  React.DetailedHTMLProps<React.InputHTMLAttributes<any>, HTMLInputElement>,
  {
    ref?:
      | ((instance: HTMLInputElement | null) => void)
      | RefObject<HTMLInputElement>
      | null
      | undefined;
    prefix?: ReactElement;
  }
>;
